import {
  PSMS_AR_SUMMARY_REQUEST,
  PSMS_RECOVERY_RATES_TREND_REQUEST,
  PSMS_SETTLEMENTS_DETAILS_REQUEST,
} from '@src/middleware/actions/actionTypes';
import {
  getARSummaryFailure,
  getARSummaryLoading,
  getARSummarySuccess,
  getRecoveryRatesTrendFailure,
  getRecoveryRatesTrendLoading,
  getRecoveryRatesTrendSuccess,
  getSettlementsDetailsFailure,
  getSettlementsDetailsLoading,
  getSettlementsDetailsSuccess,
} from '@src/middleware/actions/psms/accountsReceivableActions';
import {
  getARSummary,
  getRecoveryRatesTrendMetrics,
  getSettlementsDetails,
} from '@src/middleware/services/psms/accountsReceivable.service';
import { call, put, takeLatest } from 'redux-saga/effects';

/**
 * Saga worker to fetch recovery rates trend metrics
 * @param action - Action object containing the payload with filter parameters
 */
export function* fetchRecoveryRatesTrendMetrics(action: any) {
  const payload = action?.payload || {};

  // Set loading state to true
  yield put(getRecoveryRatesTrendLoading(true));

  try {
    // Call the API service
    const response = yield call(getRecoveryRatesTrendMetrics, payload);

    // Check if response has data
    if (response && response.Data) {
      // Dispatch success action with the API response data
      yield put(getRecoveryRatesTrendSuccess(response.Data));
    } else {
      // If no data, dispatch failure action
      yield put(getRecoveryRatesTrendFailure('No data returned from API'));
    }
  } catch (error: any) {
    // Handle error case
    console.error('Error in fetchRecoveryRatesTrendMetrics saga:', error);
    yield put(
      getRecoveryRatesTrendFailure(error?.message || 'Failed to fetch recovery rates trend data')
    );
  } finally {
    // Always set loading to false when done
    yield put(getRecoveryRatesTrendLoading(false));
  }
}

/**
 * Saga worker to fetch settlements details (makes 3 parallel API calls)
 * @param action - Action object containing the payload with filter parameters
 */
export function* fetchSettlementsDetails(action: any) {
  const payload = action?.payload || {};

  // Set loading state to true
  yield put(getSettlementsDetailsLoading(true));

  try {
    // Call the API service (handles 3 parallel calls internally)
    const response = yield call(getSettlementsDetails, payload);

    // Check if response has data and no error
    if (
      response &&
      !response.error &&
      (response.total?.Details?.length > 0 ||
        response.open?.Details?.length > 0 ||
        response.closed?.Details?.length > 0)
    ) {
      // Dispatch success action with combined data
      yield put(getSettlementsDetailsSuccess(response));
    } else if (response && response.error) {
      // If API returned error
      yield put(
        getSettlementsDetailsFailure(
          response.error?.message || 'Failed to fetch settlements details'
        )
      );
    } else {
      // If no data in any response
      yield put(getSettlementsDetailsSuccess(response)); // Allow empty state
    }
  } catch (error: any) {
    // Handle error case
    console.error('Error in fetchSettlementsDetails saga:', error);
    yield put(
      getSettlementsDetailsFailure(error?.message || 'Failed to fetch settlements details')
    );
  } finally {
    // Always set loading to false when done
    yield put(getSettlementsDetailsLoading(false));
  }
}

/**
 * Watcher saga for accounts receivable actions
 */
export function* fetchARSummary(action: any) {
  yield put(getARSummaryLoading(true));
  try {
    const response = yield call(getARSummary, action?.payload || {});
    if (response && !response.error) {
      yield put(getARSummarySuccess(response));
    } else {
      yield put(getARSummaryFailure(response?.error?.message || 'Failed to fetch AR summary'));
    }
  } catch (error: any) {
    yield put(getARSummaryFailure(error?.message || 'Failed to fetch AR summary'));
  } finally {
    yield put(getARSummaryLoading(false));
  }
}

export function* accountsReceivableSaga() {
  yield takeLatest(PSMS_RECOVERY_RATES_TREND_REQUEST, fetchRecoveryRatesTrendMetrics);
  yield takeLatest(PSMS_SETTLEMENTS_DETAILS_REQUEST, fetchSettlementsDetails);
  yield takeLatest(PSMS_AR_SUMMARY_REQUEST, fetchARSummary);
}
